import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Car, Clock, Wrench, CheckCircle, RotateCcw, FileText, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { API } from '@/utils/api';
import axios from 'axios';

const CATEGORY_LABELS = {
  mecanique: 'Mecanique',
  carrosserie: 'Carrosserie',
  assurance: 'Assurance',
};

const SignWorkOrderPage = () => {
  const { woId } = useParams();
  const sigRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/work-orders/${woId}/public`);
        setData(res.data);
        if (res.data.already_signed) setDone(true);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [woId]);

  const handleSign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    setSubmitting(true);
    try {
      const signature = sigRef.current.toDataURL('image/png');
      await axios.post(`${API}/work-orders/${woId}/sign`, { signature });
      setDone(true);
    } catch { /* ignore */ }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <p className="text-slate-400">Chargement...</p>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="p-8 rounded-2xl text-center max-w-md">
        <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p className="text-slate-600">Fiche de travail introuvable.</p>
      </Card>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
      <Card className="p-8 rounded-2xl text-center max-w-md shadow-lg" data-testid="sign-thank-you">
        <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Merci !</h1>
        <p className="text-slate-600 mb-2">Votre fiche de travail a ete signee et validee.</p>
        <p className="text-sm text-slate-500">Tout travaux supplementaires vous seront communiques par message pour confirmation.</p>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4" data-testid="sign-workorder-page">
      <div className="max-w-lg mx-auto space-y-5 py-4">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900">Fiche de travail</h1>
          <p className="text-sm text-slate-500 mt-1">Veuillez verifier et signer</p>
        </div>

        {/* Vehicle & Date */}
        <Card className="p-4 rounded-xl space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0">
              <Car className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-slate-900" data-testid="sign-client-name">{data.client_name}</p>
              <p className="text-sm text-slate-500">{data.vehicle_info}</p>
            </div>
          </div>
          {data.date && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Clock className="w-4 h-4 text-slate-400" />
              <span>{data.date} a {data.time}</span>
            </div>
          )}
          {data.category && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Wrench className="w-4 h-4 text-slate-400" />
              <span>{CATEGORY_LABELS[data.category] || data.category}</span>
            </div>
          )}
        </Card>

        {/* Travaux prevus */}
        <Card className="p-4 rounded-xl space-y-2">
          <h2 className="text-sm font-bold text-slate-900">Travaux prevus</h2>
          {data.travaux_prevus && data.travaux_prevus.length > 0 ? (
            <ul className="space-y-1">
              {data.travaux_prevus.map((t, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Selon diagnostic</p>
          )}
          {data.description && (
            <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-2 mt-2">{data.description}</p>
          )}
        </Card>

        {/* Disclaimer */}
        <Card className="p-4 rounded-xl bg-amber-50 border border-amber-100" data-testid="sign-disclaimer">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Tout travaux supplementaires constates lors de l'intervention vous seront communiques par message et soumis a votre confirmation avant execution.
            </p>
          </div>
        </Card>

        {/* Signature */}
        <Card className="p-4 rounded-xl space-y-3" data-testid="sign-signature-area">
          <h2 className="text-sm font-bold text-slate-900">Votre signature</h2>
          <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white touch-none">
            <SignatureCanvas
              ref={sigRef}
              canvasProps={{
                className: 'w-full h-48',
                'data-testid': 'sign-canvas',
              }}
              penColor="#0F172A"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => sigRef.current?.clear()}
              data-testid="sign-clear-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Effacer
            </Button>
          </div>
        </Card>

        {/* Submit */}
        <Button
          onClick={handleSign}
          disabled={submitting}
          className="w-full py-6 text-base rounded-xl bg-slate-900 hover:bg-slate-800"
          data-testid="sign-submit-btn"
        >
          {submitting ? 'Envoi en cours...' : 'Signer et valider la fiche'}
        </Button>
      </div>
    </div>
  );
};

export default SignWorkOrderPage;
